import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronDown, FolderOpen, Search } from 'lucide-react';
import { useGetMyInfoQuery } from 'src/hooks/useGetMyInfoQuery';

import classes from './style.module.css';

export const SimpleProjectSelector = () => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { data: myInfo } = useGetMyInfoQuery();
  const inputRef = useRef<HTMLInputElement>(null);

  const projectRoles = myInfo?.projectRoles ?? {};
  const currentProject = projectId ? projectRoles[projectId] : null;
  const projectName = currentProject?.project?.name || 'Select Project';

  const allProjects = Object.entries(projectRoles);
  const showSearch = allProjects.length > 5;

  // Filter projects based on search term
  const filteredProjects = allProjects.filter(([, projectRole]) =>
    projectRole.project.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleProjectSelect = (selectedProjectId: string) => {
    navigate(`/projects/${selectedProjectId}`);
    setOpen(false);
    setSearchTerm('');
  };

  const handleOpen = () => {
    setOpen(!open);
    setSearchTerm('');
  };

  // Focus search input when dropdown opens
  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest(`.${classes.container}`)) {
        setOpen(false);
        setSearchTerm('');
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  return (
    <div className={classes.container}>
      <button
        type="button"
        className={classes.selector}
        onClick={handleOpen}
      >
        <FolderOpen size={20} className={classes.icon} />
        <span className={classes.projectName}>{projectName}</span>
        <ChevronDown size={16} className={classes.chevron} />
      </button>

      {open && (
        <div className={classes.dropdown}>
          {showSearch && (
            <div className={classes.searchContainer}>
              <Search size={16} className={classes.searchIcon} />
              <input
                ref={inputRef}
                type="text"
                placeholder="Search projects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={classes.searchInput}
              />
            </div>
          )}
          <div className={classes.projectList}>
            {filteredProjects.length > 0 ? (
              filteredProjects.map(([id, projectRole]) => (
                <button
                  key={id}
                  type="button"
                  className={`${classes.projectOption} ${id === projectId ? classes.active : ''}`}
                  onClick={() => handleProjectSelect(id)}
                >
                  {projectRole.project.name}
                </button>
              ))
            ) : (
              <div className={classes.noResults}>No projects found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
